/**
 * useMapInitialPosition Hook
 *
 * React hook that handles initial map positioning based on encoded boundary.
 * Uses Google Maps native fitBounds() for optimal zoom and center calculation.
 *
 * Key behaviors:
 * - Only runs once when map is ready
 * - Uses map.fitBounds() for automatic optimal zoom calculation
 * - Positions map synchronously (no animation)
 * - Does NOT update query params
 * - Stores initial view for potential restoration
 * - Distinguishes initialization from user interaction
 */

import { useEffect, useRef, useState } from 'react';
import type { EncodedBoundary, MapView } from '@/src/lib/map/mapViewTypes';
import { calculateBoundsFromEncodedBoundary } from '@/src/lib/map/mapBoundsCalculator';
import {
  getMapInitialPositionController,
  type MapInitialPositionController,
} from '@/src/lib/map/mapInitialPositionController';
import { logBounds, logCenter, logEvent, logMapBounds, logWarning, logZoom } from '@/src/lib/map/mapDebug';

interface UseMapInitialPositionProps {
  /**
   * Encoded boundary from API (single string, array, or 2D array)
   */
  encodedBoundary?: EncodedBoundary;

  /**
   * Google Maps instance
   */
  map?: google.maps.Map | null;

  /**
   * Padding in pixels around the boundary (default: 80)
   * Passed to Google Maps fitBounds() for optimal viewport spacing
   */
  paddingPixels?: number;

  /**
   * Custom controller instance (mainly for testing)
   */
  controller?: MapInitialPositionController;
}

interface UseMapInitialPositionResult {
  /**
   * The calculated initial view (center + zoom)
   */
  initialView: MapView | null;

  /**
   * Whether the map is ready for display
   */
  isReady: boolean;
}

/**
 * Hook to calculate and apply initial map positioning
 *
 * @param props Configuration for initial positioning
 * @returns Initial view and ready signal
 */
export function useMapInitialPosition({
  encodedBoundary,
  map,
  paddingPixels = 80,
  controller = getMapInitialPositionController(),
}: UseMapInitialPositionProps): UseMapInitialPositionResult {
  const [initialView, setInitialView] = useState<MapView | null>(null);
  const [isReady, setIsReady] = useState(false);
  const hasFitRef = useRef(false);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const finalIdleListenerRef = useRef<google.maps.MapsEventListener | null>(null);

  useEffect(() => {
    // Can't do anything without map and boundary
    if (!map || !encodedBoundary) {
      return;
    }

    logEvent('MAP_INITIAL_POSITION_START', { encodedBoundary });

    // Already positioned
    if (hasFitRef.current) {
      return;
    }

    // Handler for when map becomes idle (ready to measure)
    const handleIdle = () => {
      if (hasFitRef.current) {
        // Already processed
        return;
      }

      try {
        logEvent('MAP_INITIAL_POSITION_START', { encodedBoundary });

        // Calculate bounds from encoded boundary
        const bounds = calculateBoundsFromEncodedBoundary(encodedBoundary);
        logBounds('ENCODED BOUNDARY BOUNDS', bounds);

        // Create Google Maps LatLngBounds object
        const googleBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(bounds.sw.lat, bounds.sw.lng),
          new google.maps.LatLng(bounds.ne.lat, bounds.ne.lng)
        );

        // Use Google Maps fitBounds with padding
        // fitBounds automatically calculates the best zoom level
        map.fitBounds(googleBounds, paddingPixels);

        logEvent('MAP_POSITIONED_WITH_FITBOUNDS', { paddingPixels });

        // Get the resulting view
        const center = map.getCenter();
        const zoom = map.getZoom();
        if (center && zoom !== undefined) {
          const view = { center: { lat: center.lat(), lng: center.lng() }, zoom };
          setInitialView(view);
          controller.setInitialView(view);
          logZoom('ACTUAL ZOOM FROM FITBOUNDS', zoom);
          logCenter('ACTUAL CENTER FROM FITBOUNDS', view.center);
        }

        hasFitRef.current = true;

        // Clean up initial idle listener
        if (idleListenerRef.current) {
          idleListenerRef.current.remove();
        }

        // Wait for one more idle after positioning to ensure settled
        finalIdleListenerRef.current = map.addListener('idle', () => {
          logEvent('MAP_READY_FINAL_IDLE');
          const finalBounds = map.getBounds();
          if (finalBounds) {
            logMapBounds('FINAL MAP BOUNDS', finalBounds);
          }
          setIsReady(true);
          controller.markInitializationComplete();
          if (finalIdleListenerRef.current) {
            finalIdleListenerRef.current.remove();
          }
        });
      } catch (error) {
        // Even on error, show the map
        logWarning('ERROR_DURING_POSITIONING', { error: String(error) });
        setIsReady(true);
        controller.markInitializationComplete();
        hasFitRef.current = true;

        // Clean up initial listener
        if (idleListenerRef.current) {
          idleListenerRef.current.remove();
        }
      }
    };

    // Add idle listener
    idleListenerRef.current = map.addListener('idle', handleIdle);

    // Cleanup
    return () => {
      if (idleListenerRef.current) {
        idleListenerRef.current.remove();
      }
      if (finalIdleListenerRef.current) {
        finalIdleListenerRef.current.remove();
      }
    };
  }, [map, encodedBoundary, paddingPixels, controller]);

  return {
    initialView,
    isReady,
  };
}
